import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Code,
  Smartphone,
  Monitor,
  Cloud,
  Zap,
  Shield,
  Users,
  TrendingUp,
  CheckCircle,
  ArrowRight,
  Laptop,
  Globe,
  Database,
  Cpu,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { openExternalLink } from "../../../services/site/linkService";
import { InfoService } from "../../../services/site/InfoService";
import "./Services.scss";

const ApplicationDevelopment: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("web");

  // Hero Section
  const HeroSection: React.FC = () => {
    return (
      <section className="sd_service-hero">
        <div className="container">
          <motion.div
            className="sd_service-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              className="sd_service-badge"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Code size={20} /> Custom Development
            </motion.div>

            <h1 className="sd_service-hero-title">
              Transform Your Ideas into{" "}
              <span className="gradient-text">Powerful Applications</span>
            </h1>
            <p className="sd_service-hero-subtitle">
              Expert application development services for web, mobile, and
              desktop platforms. We build scalable, secure, and innovative
              software solutions tailored to your business needs.
            </p>

            <motion.div
              className="sd_service-tech-stack"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className="sd_service-tech-icon">
                <Monitor size={24} />
                <span>Web Apps</span>
              </div>
              <div className="sd_service-tech-icon">
                <Smartphone size={24} />
                <span>Mobile Apps</span>
              </div>
              <div className="sd_service-tech-icon">
                <Laptop size={24} />
                <span>Desktop Apps</span>
              </div>
              <div className="sd_service-tech-icon">
                <Cloud size={24} />
                <span>Cloud Solutions</span>
              </div>
            </motion.div>

            <div className="sd_service-hero-buttons">
              <button
                onClick={() => navigate("/contact-us")}
                className="btn btn-primary"
              >
                Get Started <ArrowRight size={20} />
              </button>
              <button
                onClick={() => navigate("/pricing")}
                className="btn btn-secondary"
              >
                View Pricing
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    );
  };

  // Services Grid
  const ServicesGrid: React.FC = () => {
    const services = [
      {
        icon: <Globe size={32} />,
        title: "Web Application Development",
        description:
          "Build responsive, fast, and scalable web applications using modern frameworks like React, Angular, Vue.js, and Node.js.",
        features: [
          "Progressive Web Apps (PWA)",
          "Single Page Applications (SPA)",
          "E-commerce Platforms",
          "Enterprise Web Solutions",
          "API Development & Integration",
        ],
        color: "#4A90E2",
      },
      {
        icon: <Smartphone size={32} />,
        title: "Mobile App Development",
        description:
          "Create native and cross-platform mobile applications for iOS and Android with seamless user experiences.",
        features: [
          "Native iOS & Android Apps",
          "React Native Development",
          "Flutter Applications",
          "Hybrid Mobile Solutions",
          "App Store Optimization",
        ],
        color: "#E94B3C",
      },
      {
        icon: <Laptop size={32} />,
        title: "Desktop Application Development",
        description:
          "Develop powerful desktop applications for Windows, macOS, and Linux with rich functionality and performance.",
        features: [
          "Cross-platform Desktop Apps",
          "Electron Applications",
          "Native Windows Apps",
          "macOS Applications",
          "Enterprise Software",
        ],
        color: "#50C878",
      },
      {
        icon: <Database size={32} />,
        title: "Custom Software Solutions",
        description:
          "Tailored software development to meet your unique business requirements and streamline operations.",
        features: [
          "CRM & ERP Systems",
          "Business Process Automation",
          "Custom Integrations",
          "Legacy System Modernization",
          "Workflow Management Systems",
        ],
        color: "#9B59B6",
      },
      {
        icon: <Cloud size={32} />,
        title: "Cloud-Based Solutions",
        description:
          "Leverage cloud technology for scalable, flexible, and cost-effective software solutions.",
        features: [
          "AWS & Azure Integration",
          "Cloud Migration Services",
          "SaaS Development",
          "Microservices Architecture",
          "Serverless Applications",
        ],
        color: "#F39C12",
      },
      {
        icon: <Cpu size={32} />,
        title: "AI & Machine Learning Integration",
        description:
          "Integrate cutting-edge AI and ML capabilities into your applications for intelligent automation.",
        features: [
          "Predictive Analytics",
          "Natural Language Processing",
          "Computer Vision",
          "Recommendation Systems",
          "Chatbot Integration",
        ],
        color: "#1ABC9C",
      },
    ];

    return (
      <section className="sd_service-grid-section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Our Development Services</h2>
            <p className="section-subtitle">
              Comprehensive application development solutions for every platform
            </p>
          </motion.div>

          <div className="sd_service-grid">
            {services.map((service, idx) => (
              <motion.div
                key={idx}
                className="sd_service-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ y: -10 }}
              >
                <div
                  className="sd_service-card-icon"
                  style={{ color: service.color }}
                >
                  {service.icon}
                </div>
                <h3 className="sd_service-card-title">{service.title}</h3>
                <p className="sd_service-card-desc">{service.description}</p>
                <ul className="sd_service-card-features">
                  {service.features.map((feature, fIdx) => (
                    <li key={fIdx}>
                      <CheckCircle size={16} /> {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // Technology Stack Section
  const TechnologyStack: React.FC = () => {
    const technologies = {
      web: [
        { name: "React.js", logo: "⚛️" },
        { name: "Angular", logo: "🅰️" },
        { name: "Vue.js", logo: "💚" },
        { name: "Node.js", logo: "🟢" },
        { name: "Python", logo: "🐍" },
        { name: "Django", logo: "🎸" },
        { name: "ASP.NET", logo: "🔷" },
        { name: "PHP", logo: "🐘" },
      ],
      mobile: [
        { name: "React Native", logo: "⚛️" },
        { name: "Flutter", logo: "💙" },
        { name: "Swift", logo: "🍎" },
        { name: "Kotlin", logo: "🤖" },
        { name: "Ionic", logo: "⚡" },
        { name: "Xamarin", logo: "🔵" },
      ],
      desktop: [
        { name: "Electron", logo: "⚡" },
        { name: ".NET", logo: "🔷" },
        { name: "Java", logo: "☕" },
        { name: "C++", logo: "➕" },
        { name: "Python", logo: "🐍" },
        { name: "Qt", logo: "🎨" },
      ],
      database: [
        { name: "PostgreSQL", logo: "🐘" },
        { name: "MongoDB", logo: "🍃" },
        { name: "MySQL", logo: "🐬" },
        { name: "Redis", logo: "🔴" },
        { name: "Firebase", logo: "🔥" },
        { name: "Oracle", logo: "🔶" },
      ],
    };

    return (
      <section className="sd_service-tech-section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Our Technology Stack</h2>
            <p className="section-subtitle">
              We use cutting-edge technologies to build robust applications
            </p>
          </motion.div>

          <div className="sd_service-tech-tabs">
            <div className="tabs is-centered is-boxed">
              <ul>
                <li className={activeTab === "web" ? "is-active" : ""}>
                  <a onClick={() => setActiveTab("web")}>
                    <Globe size={18} /> Web
                  </a>
                </li>
                <li className={activeTab === "mobile" ? "is-active" : ""}>
                  <a onClick={() => setActiveTab("mobile")}>
                    <Smartphone size={18} /> Mobile
                  </a>
                </li>
                <li className={activeTab === "desktop" ? "is-active" : ""}>
                  <a onClick={() => setActiveTab("desktop")}>
                    <Laptop size={18} /> Desktop
                  </a>
                </li>
                <li className={activeTab === "database" ? "is-active" : ""}>
                  <a onClick={() => setActiveTab("database")}>
                    <Database size={18} /> Database
                  </a>
                </li>
              </ul>
            </div>

            <motion.div
              className="sd_service-tech-grid"
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {technologies[activeTab as keyof typeof technologies].map(
                (tech, idx) => (
                  <motion.div
                    key={idx}
                    className="sd_service-tech-item"
                    whileHover={{ scale: 1.1 }}
                  >
                    <span className="tech-logo">{tech.logo}</span>
                    <span className="tech-name">{tech.name}</span>
                  </motion.div>
                )
              )}
            </motion.div>
          </div>
        </div>
      </section>
    );
  };

  // Development Process
  const DevelopmentProcess: React.FC = () => {
    const steps = [
      {
        number: "01",
        title: "Discovery & Planning",
        description:
          "We analyze your requirements, define project scope, and create a strategic roadmap.",
        icon: <Users size={32} />,
      },
      {
        number: "02",
        title: "Design & Prototyping",
        description:
          "Our designers create intuitive UI/UX designs and interactive prototypes for validation.",
        icon: <Monitor size={32} />,
      },
      {
        number: "03",
        title: "Development & Testing",
        description:
          "Expert developers build your application with rigorous testing at every stage.",
        icon: <Code size={32} />,
      },
      {
        number: "04",
        title: "Deployment & Support",
        description:
          "We deploy your application and provide ongoing maintenance and support.",
        icon: <Zap size={32} />,
      },
    ];

    return (
      <section className="sd_service-process-section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Our Development Process</h2>
            <p className="section-subtitle">
              A proven methodology for successful project delivery
            </p>
          </motion.div>

          <div className="sd_service-process-timeline">
            {steps.map((step, idx) => (
              <motion.div
                key={idx}
                className="sd_service-process-step"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.2 }}
              >
                <div className="step-number">{step.number}</div>
                <div className="step-content">
                  <div className="step-icon">{step.icon}</div>
                  <h3 className="step-title">{step.title}</h3>
                  <p className="step-description">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // Why Choose Us
  const WhyChooseUs: React.FC = () => {
    const benefits = [
      {
        icon: <Shield size={32} />,
        title: "Security First",
        description: "Enterprise-grade security with industry best practices",
      },
      {
        icon: <Zap size={32} />,
        title: "Fast Delivery",
        description: "Agile methodology ensures quick turnaround times",
      },
      {
        icon: <TrendingUp size={32} />,
        title: "Scalable Solutions",
        description: "Built to grow with your business requirements",
      },
      {
        icon: <Users size={32} />,
        title: "Expert Team",
        description: "Experienced developers with proven track records",
      },
      {
        icon: <CheckCircle size={32} />,
        title: "Quality Assurance",
        description: "Rigorous testing ensures bug-free applications",
      },
      {
        icon: <Monitor size={32} />,
        title: "24/7 Support",
        description: "Round-the-clock technical support and maintenance",
      },
    ];

    return (
      <section className="sd_service-benefits-section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">
              Why Choose Our Application Development Services?
            </h2>
            <p className="section-subtitle">
              Industry-leading expertise with a commitment to excellence
            </p>
          </motion.div>

          <div className="sd_service-benefits-grid">
            {benefits.map((benefit, idx) => (
              <motion.div
                key={idx}
                className="sd_service-benefit-card"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ scale: 1.05 }}
              >
                <div className="benefit-icon">{benefit.icon}</div>
                <h3 className="benefit-title">{benefit.title}</h3>
                <p className="benefit-description">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // CTA Section
  const CTASection: React.FC = () => {
    return (
      <section className="sd_service-cta-section">
        <div className="container">
          <motion.div
            className="sd_service-cta-content"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="cta-title">
              Ready to Build Your Next Application?
            </h2>
            <p className="cta-text">
              Let's turn your vision into reality. Contact us today for a free
              consultation and project estimate.
            </p>
            <div className="cta-buttons">
              <button
                className="btn btn-primary"
                onClick={() => navigate("/contact-us")}
              >
                Get Free Consultation <ArrowRight size={20} />
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => openExternalLink(InfoService?.loginLink)}
              >
                View Portfolio
              </button>
            </div>
            <div className="cta-stats">
              <div className="stat-item">
                <span className="stat-number">500+</span>
                <span className="stat-label">Projects Delivered</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">98%</span>
                <span className="stat-label">Client Satisfaction</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">50+</span>
                <span className="stat-label">Expert Developers</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    );
  };

  return (
    <div className="sd_service-container">
      <HeroSection />
      <ServicesGrid />
      <TechnologyStack />
      <DevelopmentProcess />
      <WhyChooseUs />
      <CTASection />
    </div>
  );
};

export default ApplicationDevelopment;
